import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, Typography, Box, Button } from "@mui/material";
import {
  AlertComponent,
  BreadcrumbComponent,
} from "../../../common/components";
import { breadcrumbLinkSocialImport } from "../lead-list.constant";
import FacebookIcon from "../../../assets/icons/facebook.svg";
import LinkedInIcon from "../../../assets/icons/linkedin.svg";
import EastIcon from "@mui/icons-material/East";
import "./social-import.scss";
import {
  facebookLogin,
  facebookUserTokenStatus,
} from "../../../api/social-media-import";
import { useSetLoading } from "../../../common/hooks/appearance";

const socialPlatforms = [
  {
    key: "facebook",
    name: "Facebook Ads Sync",
    description: "Sync leads from your Facebook platform to AFP CRM using",
    icon: <img src={FacebookIcon} className="facebook-icon" />,
  },
  {
    key: "linkedin",
    name: "LinkedIn Ads Sync",
    description: "Sync leads from your LinkedIn platform to AFP CRM using",
    icon: <img src={LinkedInIcon} className="linkedin-icon" />,
    comingSoon: true,
  },
];

const handleSetup = async (platformKey: string) => {
  if (platformKey !== "facebook") return;

  try {
    const response = await facebookLogin();
    globalThis.location.href = response.data.data;
  } catch (error) {
    console.error("Error during setup:", error);
  }
};

const SocialImport: React.FC = () => {
  const [fbStatus, setFbStatus] = React.useState<
    "Setup now" | "connected" | "reconnect"
  >("Setup now");
  const [isLoading, setIsLoading] = React.useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<
    "success" | "error" | "info" | "warning"
  >("success");

  useEffect(() => {
    const checkFacebookTokenStatus = async () => {
      try {
        setIsLoading(true);
        const response = await facebookUserTokenStatus();
        if (!response.data.data.data) {
          setFbStatus("Setup now");
        } else if (response.data.data.data.is_valid) {
          setFbStatus("connected");
        } else {
          setFbStatus("reconnect");
        }
      } catch (error) {
        console.error("Error checking Facebook user token status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkFacebookTokenStatus();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const fbStatus = params.get("fb");

    if (fbStatus === "connected") {
      setAlertMessage("Facebook connected successfully");
      setAlertType("success");
      setAlertOpen(true);
    } else if (fbStatus === "error") {
      setAlertMessage("Failed to connect Facebook");
      setAlertType("error");
      setAlertOpen(true);
    } else if (fbStatus === "cancelled") {
      setAlertMessage("Facebook login was cancelled");
      setAlertType("warning");
      setAlertOpen(true);
    }

    if (fbStatus) {
      globalThis.history.replaceState(
        {},
        document.title,
        globalThis.location.pathname,
      );
    }
  }, []);

  const getButtonText = (platform: string) => {
    if (platform !== "facebook") {
      const platformData = socialPlatforms.find((p) => p.key === platform);
      return platformData?.comingSoon ? "Coming Soon" : "Setup now";
    }

    switch (fbStatus) {
      case "connected":
        return "Connected";
      case "reconnect":
        return "Reconnect";
      default:
        return "Setup now";
    }
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <div>
      <div className="breadcrumb-wrapper">
        <BreadcrumbComponent
          breadcrumbLink={breadcrumbLinkSocialImport()}
          showBackArrow={true}
        />
      </div>
      <Box className="social-import-wrapper">
        <Typography variant="h5" className="social-import-title">
          Choose your favourite social media platforms to import data
          seamlessly.
        </Typography>
        <Typography variant="body1" className="social-import-subtitle">
          Connect your accounts and effortlessly manage leads and customer
          details with just a few clicks!
        </Typography>

        <Box className="social-import-card-group">
          {socialPlatforms.map((platform, index) => (
            <Card key={index} className="social-import-card">
              <div className="social-import-icon">{platform.icon}</div>
              <CardContent className="social-import-content">
                <Typography variant="h6" className="social-import-header">
                  {platform.name}
                </Typography>
                <Typography
                  variant="body2"
                  className="social-import-description"
                >
                  {platform.description}
                </Typography>
                <Button
                  variant="text"
                  className="setup-button"
                  endIcon={<EastIcon style={{ fontSize: "14px" }} />}
                  onClick={() => handleSetup(platform.key)}
                  disabled={
                    platform.comingSoon ||
                    (platform.key === "facebook" && fbStatus === "connected")
                  }
                >
                  {getButtonText(platform.key)}
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default SocialImport;
